import { useState } from 'react';
import type { InputKind } from '../../engine/types';
import { inputManager } from '../../input/manager';
import { MicInputSource } from '../../input/mic';
import { MidiInputSource } from '../../input/midi';
import type { AudioProcessingInfo, InputStatus } from '../../input/types';
import { settingsStore } from '../../store/settings';
import { useInputStatus, useSettings } from '../hooks/stores';
import { activateInput, INPUT_LABELS } from '../inputControl';

export function InputPicker({ compact = false }: { compact?: boolean }) {
  const status = useInputStatus();
  const settings = useSettings();
  const [busy, setBusy] = useState(false);
  const midiSupported = MidiInputSource.isSupported();
  const micSupported = MicInputSource.isSupported();
  const activeKind = inputManager.currentKind;
  const kinds: InputKind[] = settings.developer ? ['mic', 'midi', 'keyboard', 'synth'] : ['mic', 'midi', 'keyboard'];

  const choose = async (kind: InputKind) => {
    setBusy(true);
    try {
      await activateInput(kind);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={compact ? 'input-picker compact' : 'input-picker'}>
      <div className="seg" role="group" aria-label="Source d’entrée">
        {kinds.map((kind) => {
          const unsupported = (kind === 'midi' && !midiSupported) || (kind === 'mic' && !micSupported);
          return (
            <button
              key={kind}
              type="button"
              className={activeKind === kind ? 'seg-btn on' : 'seg-btn'}
              disabled={unsupported || busy}
              title={unsupported ? 'Non pris en charge par ce navigateur' : undefined}
              onClick={() => void choose(kind)}
            >
              {INPUT_LABELS[kind]}
            </button>
          );
        })}
      </div>
      <StatusLine status={status} active={activeKind !== null} />
      {!compact && <DeviceSelect status={status} />}
      {!compact && status.kind === 'mic' && status.state === 'active' && status.processing && <ProcessingInfo p={status.processing} />}
    </div>
  );
}

function StatusLine({ status, active }: { status: InputStatus; active: boolean }) {
  const label = active ? status.message : 'Aucune source active. Choisissez comment l’application vous écoute.';
  return (
    <div className="status-line">
      <span className={`status-dot ${active ? status.state : 'idle'}`} aria-hidden="true" />
      <span className="status-text">{label}</span>
      {active && status.level && <LevelMeter db={status.level.db} noise={status.level.noise} />}
    </div>
  );
}

export function LevelMeter({ db, noise }: { db: number; noise: number }) {
  const pct = (v: number) => Math.max(0, Math.min(100, ((v + 60) / 60) * 100));
  return (
    <span className="level-meter" title={`Niveau ${Math.round(db)} dB, bruit de fond ${Math.round(noise)} dB`} aria-label="Niveau du micro">
      <span className="level-fill" style={{ width: `${pct(db)}%` }} />
      <span className="level-noise" style={{ left: `${pct(noise)}%` }} />
    </span>
  );
}

function DeviceSelect({ status }: { status: InputStatus }) {
  const settings = useSettings();
  if (status.state !== 'active' || status.devices.length === 0) return null;
  if (status.kind === 'midi') {
    return (
      <label className="field inline">
        <span>Piano MIDI</span>
        <select
          value={settings.midiDeviceId ?? ''}
          onChange={(e) => {
            const id = e.target.value || null;
            settingsStore.set({ midiDeviceId: id });
            inputManager.midi?.selectDevice(id);
          }}
        >
          <option value="">Tous les périphériques</option>
          {status.devices.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      </label>
    );
  }
  if (status.kind === 'mic' && status.devices.length > 1) {
    return (
      <label className="field inline">
        <span>Micro</span>
        <select
          value={settings.micDeviceId ?? ''}
          onChange={(e) => {
            settingsStore.set({ micDeviceId: e.target.value || null });
            void activateInput('mic');
          }}
        >
          <option value="">Micro par défaut</option>
          {status.devices.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      </label>
    );
  }
  return null;
}

function ProcessingInfo({ p }: { p: AudioProcessingInfo }) {
  const onOff = (v: boolean | undefined) => (v === undefined ? 'inconnu' : v ? 'actif' : 'désactivé');
  const imposed = p.echoCancellation === true || p.noiseSuppression === true || p.autoGainControl === true;
  return (
    <p className={imposed ? 'muted warn' : 'muted'}>
      Prétraitements du navigateur : annulation d’écho {onOff(p.echoCancellation)}, réduction de bruit {onOff(p.noiseSuppression)}, gain automatique {onOff(p.autoGainControl)}
      {p.sampleRate ? `, ${p.sampleRate} Hz` : ''}.
      {imposed ? ' Le navigateur impose des prétraitements : la détection peut être moins précise.' : ''}
    </p>
  );
}
